/**
 * Stone Investment Blog Hero
 *
 * Legacy BlogHero with Stone's cinematic aesthetic:
 * dark overlay, Cormorant Garamond title, editorial feel.
 */

'use client';

import type { BlogHeroProps } from '@routeroyal/theme-core';
import Image from 'next/image';
import { StoneBlogSearch } from './StoneBlogSearch';

interface StoneBlogHeroProps extends BlogHeroProps {
  messages?: Record<string, unknown>;
}

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

export function StoneBlogHero({
  title,
  subtitle,
  backgroundImage,
  showSearch = false,
  onSearch,
  messages,
}: StoneBlogHeroProps) {
  const stoneMsg = messages?.stone as Record<string, unknown> | undefined;
  const blogMsg = stoneMsg?.blog as Record<string, unknown> | undefined;
  const searchMsg = blogMsg?.search as Record<string, unknown> | undefined;

  return (
    <section className="relative py-28 lg:py-36 overflow-hidden">
      {backgroundImage && (
        <div className="absolute inset-0 z-0">
          <Image
            src={backgroundImage}
            alt="Journal"
            fill
            className="object-cover grayscale"
            priority
          />
          <div className="absolute inset-0 bg-linear-to-b from-stone-900/80 to-stone-900/60" />
        </div>
      )}

      {!backgroundImage && (
        <div className="absolute inset-0 bg-stone-900" />
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <p
            className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-6"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Journal
          </p>
          <h1
            className="text-5xl md:text-6xl lg:text-7xl text-white mb-6"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              className="text-xl text-stone-300 mb-8 leading-relaxed"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
            >
              {subtitle}
            </p>
          )}

          {showSearch && onSearch && (
            <div className="max-w-xl mx-auto mt-8">
              <StoneBlogSearch
                onChange={onSearch}
                placeholder={safeGetString(searchMsg?.placeholder, 'Search articles...')}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
